import type {
    DropdownData,
    FilteredValue,
    RepeatableDomainAdapterContext,
    RepeatableRowExtraItem,
    RepeatableRowExtraView,
    RepeatableSubFieldDataLoader,
    RepeatableSubFieldDependencyContext,
    RepeatableSubFieldDependencyPatch,
} from '@fitogest/reactive-form-builder/interfaces';
import type { TareasBioModel } from 'src/app/models/tareas/tareas-bio.model';
import type { TratamientosControlMagramaLoader, TratamientosControlMagramaSlotData } from './form-definition';
import { TratamientosControlValueResolver } from './tratamientos-control-value-resolver';

const DEFAULT_REAL_UNIT = 'cc/100 L';

const createDropdownData = <T>(): DropdownData<T> => ({ filtered: [], values: [] });

type TratamientosControlRepeatableDropdownKey = 'uso' | 'plaga';

type MagramaMetadata = Array<{ label: string; value: string | null }>;

type TratamientosControlMagramaRowState = {
    usos: DropdownData<string>;
    plagas: DropdownData<string>;
    allPlagas: FilteredValue<string>[];
    allUds: MagramaMetadata;
    allDosis: MagramaMetadata;
    uds: MagramaMetadata;
    dosis: MagramaMetadata;
    plagasByUso: Record<string, FilteredValue<string>[]>;
    udsByUso: Record<string, MagramaMetadata>;
    dosisByUso: Record<string, MagramaMetadata>;
    productId: string;
    referencia: string;
    lastSelectedUsoKey: string;
};

export type TratamientosControlDropdownDiagnostics = {
    readonly rowIndex: number;
    readonly productId: string;
    readonly referencia: string;
    readonly selectedUsoKey: string;
    readonly usosCount: number;
    readonly visiblePlagasCount: number;
    readonly allPlagasCount: number;
    readonly usoScopedPlagasCount: number;
    readonly isUsingHydrationFallback: boolean;
};

/**
 * Coordinates repeatable dropdown dependencies for Tratamientos Control.
 *
 * The class keeps the current legacy MAGRAMA source working while isolating the
 * future declarative producto -> uso -> plaga data-source flow from the form definition.
 */
export class TratamientosControlDropdownDataSourceFactory {
    private magramaDataLoader: TratamientosControlMagramaLoader | null = null;
    private readonly magramaLoadPromises = new Map<string, Promise<TratamientosControlMagramaSlotData | null>>();
    private readonly magramaRowLoadKeys = new Map<number, string>();
    private readonly magramaRows = new Map<number, TratamientosControlMagramaRowState>();

    constructor(private readonly getProductDropdown: () => DropdownData<TareasBioModel>) {}

    public setMagramaDataLoader(loader: TratamientosControlMagramaLoader): void {
        if (this.magramaDataLoader !== loader) {
            this.clearMagramaCache();
        }

        this.magramaDataLoader = loader;
    }

    public clearMagramaCache(): void {
        this.magramaLoadPromises.clear();
        this.magramaRowLoadKeys.clear();
        this.magramaRows.clear();
    }

    public getCachedMagramaRequestCount(): number {
        return this.magramaLoadPromises.size;
    }

    public getLoadData(key: TratamientosControlRepeatableDropdownKey): RepeatableSubFieldDataLoader {
        return (ctx: RepeatableSubFieldDependencyContext): Promise<DropdownData<unknown> | void> =>
            this.loadMagramaForRepeatableDependency(ctx, key);
    }

    public getData(key: string, rowIndex: number): DropdownData<unknown> {
        const state = this.getMagramaRowState(rowIndex);

        if (key === 'uso') {
            return state.usos as DropdownData<unknown>;
        }

        if (key === 'plaga') {
            return state.plagas as DropdownData<unknown>;
        }

        return createDropdownData<unknown>();
    }

    public getRowExtraView(ctx: RepeatableDomainAdapterContext): RepeatableRowExtraView {
        const rowIndex = this.normalizeRepeatableRowIndex(ctx.rowIndex);
        const selectedPlaga = ctx.row['plaga'];
        const selectedUso = ctx.row['uso'];
        const plagaId = TratamientosControlValueResolver.resolveOptionId(selectedPlaga);

        if (!plagaId) {
            return { isVisible: false, items: [] };
        }

        const state = this.getMagramaRowState(rowIndex);
        const doseRecommendation = this.getDoseRecommendationForRow(rowIndex, selectedPlaga);
        const unit = this.getUnitForRow(rowIndex, selectedPlaga);
        const securityTerm = this.getSecurityTermForRow(rowIndex, selectedPlaga);
        const items = this.filterVisibleExtraItems([
            {
                label: 'Uso MAGRAMA',
                value: this.getSelectedOptionLabel(state.usos.filtered, selectedUso),
                styleClass: 'magrama-uso',
            },
            {
                label: 'Plaga MAGRAMA',
                value: this.getSelectedOptionLabel(state.plagas.filtered, selectedPlaga),
                styleClass: 'magrama-plaga',
            },
            { label: 'Dosis recomendada', value: this.extractDoseText(doseRecommendation), styleClass: 'magrama-dose' },
            { label: 'Ud. MAGRAMA', value: unit, styleClass: 'magrama-unit' },
            { label: 'Plazo seg.', value: securityTerm, styleClass: 'magrama-security-term' },
        ]);

        return {
            isVisible: items.length > 0,
            styleClass: 'dose-recommendation-neutral magrama-recommendation',
            items,
        };
    }

    public getDiagnostics(rowIndex: number): TratamientosControlDropdownDiagnostics {
        const normalizedRowIndex = this.normalizeRepeatableRowIndex(rowIndex);
        const state = this.getMagramaRowState(normalizedRowIndex);
        const selectedUsoKey = state.lastSelectedUsoKey;
        const usoScopedPlagasCount = selectedUsoKey ? (state.plagasByUso[selectedUsoKey]?.length ?? 0) : 0;
        const isUsingHydrationFallback = selectedUsoKey === '' &&
            state.plagas.filtered.length === state.allPlagas.length &&
            state.allPlagas.length > 0;

        return {
            rowIndex: normalizedRowIndex,
            productId: state.productId,
            referencia: state.referencia,
            selectedUsoKey,
            usosCount: state.usos.filtered.length,
            visiblePlagasCount: state.plagas.filtered.length,
            allPlagasCount: state.allPlagas.length,
            usoScopedPlagasCount,
            isUsingHydrationFallback,
        };
    }

    public patchUnitsAfterPlagaChange(ctx: RepeatableSubFieldDependencyContext): RepeatableSubFieldDependencyPatch {
        const rowIndex = this.normalizeRepeatableRowIndex(ctx.rowIndex);
        const selectedPlaga = ctx.changedValue;
        const udCuaderno = this.getUnitForRow(rowIndex, selectedPlaga);
        const currentUdCuaderno = String(ctx.row['udCuaderno'] ?? '').trim();
        const currentUdReal = String(ctx.row['udReal'] ?? '').trim();
        const patch: Record<string, unknown> = {};

        if (udCuaderno && currentUdCuaderno !== udCuaderno) {
            patch['udCuaderno'] = udCuaderno;
        }

        if (TratamientosControlValueResolver.resolveOptionId(selectedPlaga) && currentUdReal !== DEFAULT_REAL_UNIT) {
            patch['udReal'] = DEFAULT_REAL_UNIT;
        }

        return patch;
    }

    public getUnitForRow(rowIndex: number, selectedPlaga: unknown): string {
        const state = this.getMagramaRowState(rowIndex);
        const normalizedPlaga = this.normalizeLookupText(selectedPlaga);
        const unit = state.uds.find((entry) => this.normalizeLookupText(entry.label) === normalizedPlaga)?.value;

        return String(unit ?? '').trim();
    }

    public getDoseRecommendationForRow(rowIndex: number, selectedPlaga: unknown): string {
        const state = this.getMagramaRowState(rowIndex);
        const normalizedPlaga = this.normalizeLookupText(selectedPlaga);
        const dose = state.dosis.find((entry) => this.normalizeLookupText(entry.label) === normalizedPlaga)?.value;

        return String(dose ?? '').trim();
    }

    public applyMagramaRowData(
        rowIndex: number,
        data: TratamientosControlMagramaSlotData,
        productId = '',
        referencia = '',
    ): void {
        const state = this.getMagramaRowState(rowIndex);

        state.usos.filtered = [...data.usosFiltered];
        state.usos.values = [...data.usosFiltered];
        state.allPlagas = [...data.agentesFiltered];
        state.allUds = [...data.udsFiltered];
        state.allDosis = [...data.dosisFiltered];
        state.plagasByUso = { ...data.agentesByUso };
        state.udsByUso = { ...data.udsByUso };
        state.dosisByUso = { ...data.dosisByUso };
        state.plagas.filtered = [...state.allPlagas];
        state.plagas.values = [...state.allPlagas];
        state.uds = [...state.allUds];
        state.dosis = [...state.allDosis];
        state.productId = productId;
        state.referencia = referencia;
    }

    public clearMagramaRowData(rowIndex: number): void {
        const state = this.getMagramaRowState(rowIndex);

        state.usos.filtered = [];
        state.usos.values = [];
        state.plagas.filtered = [];
        state.plagas.values = [];
        state.allPlagas = [];
        state.allUds = [];
        state.allDosis = [];
        state.uds = [];
        state.dosis = [];
        state.plagasByUso = {};
        state.udsByUso = {};
        state.dosisByUso = {};
        state.productId = '';
        state.referencia = '';
        state.lastSelectedUsoKey = '';
    }

    private async loadMagramaForRepeatableDependency(
        ctx: RepeatableSubFieldDependencyContext,
        targetKey: TratamientosControlRepeatableDropdownKey,
    ): Promise<DropdownData<unknown> | void> {
        const rowIndex = this.normalizeRepeatableRowIndex(ctx.rowIndex);
        const slot = this.resolveRepeatableSlot(rowIndex);
        const product = this.resolveRepeatableProductFromContext(ctx);
        const rawProduct = product ?? (ctx.changedKey === 'producto' ? ctx.changedValue : ctx.row['producto']);
        const productId = TratamientosControlValueResolver.resolveOptionId(rawProduct);
        const referencia = TratamientosControlValueResolver.resolveProductReference(product);

        if (!this.magramaDataLoader || !productId || !referencia) {
            this.clearMagramaRowDataIfUnavailable(rowIndex, productId, referencia);

            return this.getData(targetKey, rowIndex);
        }

        const loadKey = `${productId}:${referencia}`;
        const rowLoadKey = `${rowIndex}:${loadKey}`;
        const promise = this.magramaLoadPromises.get(loadKey)
            ?? this.magramaDataLoader({ rowIndex, slot, productId, product, referencia });

        this.magramaLoadPromises.set(loadKey, promise);
        this.magramaRowLoadKeys.set(rowIndex, rowLoadKey);

        const data = await promise;

        if (this.magramaRowLoadKeys.get(rowIndex) !== rowLoadKey) {
            return this.getData(targetKey, rowIndex);
        }

        if (data) {
            this.applyMagramaRowData(rowIndex, data, productId, referencia);
            this.applyLegacyHydrationFallbackOptions(rowIndex, ctx);
        }

        if (targetKey === 'plaga') {
            this.filterPlagasByUso(rowIndex, this.resolveSelectedUso(ctx), ctx);
        }

        return this.getData(targetKey, rowIndex);
    }

    private clearMagramaRowDataIfUnavailable(rowIndex: number, productId: string, referencia: string): void {
        if (productId && referencia) {
            return;
        }

        this.magramaRowLoadKeys.delete(rowIndex);
        this.clearMagramaRowData(rowIndex);
    }

    private resolveRepeatableProductFromContext(ctx: RepeatableSubFieldDependencyContext): TareasBioModel | null {
        return ctx.resolveEntity?.<TareasBioModel>('producto')
            ?? this.resolveRepeatableProduct(ctx.row['producto'] ?? (ctx.changedKey === 'producto' ? ctx.changedValue : null));
    }

    private resolveRepeatableProduct(rawProduct: unknown): TareasBioModel | null {
        if (rawProduct !== null && typeof rawProduct === 'object') {
            return rawProduct as TareasBioModel;
        }

        const productId = TratamientosControlValueResolver.resolveOptionId(rawProduct);

        if (!productId) {
            return null;
        }

        const productDropdown = this.getProductDropdown();
        const options = [
            ...(productDropdown.filtered ?? []),
            ...(productDropdown.values ?? []),
        ] as Array<FilteredValue<TareasBioModel>>;
        const found = options.find((item) => TratamientosControlValueResolver.getProductIdentityCandidates(item.value).includes(productId));

        return found?.value ?? null;
    }

    private resolveSelectedUso(ctx: RepeatableSubFieldDependencyContext): unknown {
        return ctx.changedKey === 'uso' ? ctx.changedValue : ctx.row['uso'];
    }

    private filterPlagasByUso(
        rowIndex: number,
        selectedUso: unknown,
        ctx?: RepeatableSubFieldDependencyContext,
    ): void {
        const state = this.getMagramaRowState(rowIndex);
        const hasUsoScopedPlagas = Object.keys(state.plagasByUso).length > 0;

        if (!hasUsoScopedPlagas) {
            this.applyVisiblePlagas(
                state,
                this.appendLegacyFallbackOption(state.allPlagas, this.resolveLegacyPlagaLabel(ctx)),
                state.allUds,
                state.allDosis,
            );

            return;
        }

        const selectedUsoKey = this.normalizeLookupText(selectedUso);

        state.lastSelectedUsoKey = selectedUsoKey;

        if (!selectedUsoKey) {
            this.applyVisiblePlagas(
                state,
                this.appendLegacyFallbackOption(state.allPlagas, this.resolveLegacyPlagaLabel(ctx)),
                state.allUds,
                state.allDosis,
            );

            return;
        }

        const scopedPlagas = state.plagasByUso[selectedUsoKey] ?? this.resolveLooseUsoScopedPlagas(state, selectedUsoKey);
        const scopedUds = state.udsByUso[selectedUsoKey] ?? this.resolveLooseUsoScopedMetadata(state.udsByUso, selectedUsoKey);
        const scopedDosis = state.dosisByUso[selectedUsoKey] ?? this.resolveLooseUsoScopedMetadata(state.dosisByUso, selectedUsoKey);

        this.applyVisiblePlagas(
            state,
            this.appendLegacyFallbackOption(scopedPlagas, this.resolveLegacyPlagaLabel(ctx)),
            scopedUds,
            scopedDosis,
        );
    }


    private applyLegacyHydrationFallbackOptions(
        rowIndex: number,
        ctx: RepeatableSubFieldDependencyContext,
    ): void {
        const state = this.getMagramaRowState(rowIndex);
        const legacyUsoLabel = this.resolveLegacyUsoLabel(ctx);
        const legacyPlagaLabel = this.resolveLegacyPlagaLabel(ctx);

        state.usos.filtered = this.appendLegacyFallbackOption(state.usos.filtered, legacyUsoLabel);
        state.usos.values = this.appendLegacyFallbackOption(state.usos.values, legacyUsoLabel);
        state.allPlagas = this.appendLegacyFallbackOption(state.allPlagas, legacyPlagaLabel);
        state.plagas.filtered = this.appendLegacyFallbackOption(state.plagas.filtered, legacyPlagaLabel);
        state.plagas.values = this.appendLegacyFallbackOption(state.plagas.values, legacyPlagaLabel);
    }

    private appendLegacyFallbackOption(
        options: FilteredValue<string>[],
        label: string,
    ): FilteredValue<string>[] {
        const normalizedLabel = this.normalizeLookupText(label);

        if (!normalizedLabel) {
            return [...options];
        }

        const exists = options.some((option) => {
            const normalizedOptionValue = this.normalizeLookupText(option.value ?? option.label);
            const normalizedOptionLabel = this.normalizeLookupText(option.label);

            return normalizedOptionValue === normalizedLabel || normalizedOptionLabel === normalizedLabel;
        });

        if (exists) {
            return [...options];
        }

        return [...options, { label, value: label, raw: label, group: 'Hidratación' }];
    }

    private resolveLegacyUsoLabel(ctx?: RepeatableSubFieldDependencyContext): string {
        return this.resolveLegacyLabel(ctx, ['usoNombre', 'uso']);
    }

    private resolveLegacyPlagaLabel(ctx?: RepeatableSubFieldDependencyContext): string {
        return this.resolveLegacyLabel(ctx, ['agenteNombre', 'plaga']);
    }

    private resolveLegacyLabel(ctx: RepeatableSubFieldDependencyContext | undefined, keys: readonly string[]): string {
        if (!ctx) {
            return '';
        }

        for (const key of keys) {
            const value = ctx.row[key];

            if (typeof value === 'string' || typeof value === 'number') {
                const normalizedValue = String(value).trim();

                if (normalizedValue !== '') {
                    return normalizedValue;
                }
            }
        }

        return '';
    }

    private resolveLooseUsoScopedPlagas(
        state: TratamientosControlMagramaRowState,
        selectedUsoKey: string,
    ): FilteredValue<string>[] {
        const key = this.resolveLooseUsoKey(state.plagasByUso, selectedUsoKey);

        return key ? state.plagasByUso[key] ?? [] : [];
    }

    private resolveLooseUsoScopedMetadata(
        source: Record<string, MagramaMetadata>,
        selectedUsoKey: string,
    ): MagramaMetadata {
        const key = this.resolveLooseUsoKey(source, selectedUsoKey);

        return key ? source[key] ?? [] : [];
    }

    private resolveLooseUsoKey<T>(source: Record<string, T>, selectedUsoKey: string): string {
        if (!selectedUsoKey) {
            return '';
        }

        return Object.keys(source).find((key) => key === selectedUsoKey)
            ?? Object.keys(source).find((key) => key.includes(selectedUsoKey) || selectedUsoKey.includes(key))
            ?? '';
    }

    private applyVisiblePlagas(
        state: TratamientosControlMagramaRowState,
        plagas: FilteredValue<string>[],
        uds: MagramaMetadata,
        dosis: MagramaMetadata,
    ): void {
        state.plagas.filtered = [...plagas];
        state.plagas.values = [...plagas];
        state.uds = [...uds];
        state.dosis = [...dosis];
    }

    private getSecurityTermForRow(rowIndex: number, selectedPlaga: unknown): string {
        const doseRecommendation = this.getDoseRecommendationForRow(rowIndex, selectedPlaga);
        const match = /plazo\s*seg\.?:?\s*([^,;]+)/iu.exec(doseRecommendation);
        const securityTerm = String(match?.[1] ?? '').trim();

        return securityTerm !== '' ? securityTerm : '';
    }

    private extractDoseText(doseRecommendation: string): string {
        const normalized = doseRecommendation.trim();

        if (!normalized) {
            return '';
        }

        return normalized
            .replace(/^dosis\.?:?\s*/iu, '')
            .replace(/\s*plazo\s*seg\.?:?.*$/iu, '')
            .trim();
    }

    private getSelectedOptionLabel(options: FilteredValue<string>[], selectedValue: unknown): string {
        const normalizedValue = this.normalizeLookupText(selectedValue);

        if (!normalizedValue) {
            return '';
        }

        const match = options.find((option) => this.normalizeLookupText(option.value ?? option.label) === normalizedValue);

        return match?.label ?? String(selectedValue ?? '').trim();
    }

    private filterVisibleExtraItems(items: RepeatableRowExtraItem[]): RepeatableRowExtraItem[] {
        return items.filter((item) => item.value.trim() !== '');
    }

    private resolveRepeatableSlot(rowIndex: number): 1 | 2 | 3 {
        return Math.min((rowIndex ?? 0) + 1, 3) as 1 | 2 | 3;
    }

    private normalizeRepeatableRowIndex(rowIndex: number): number {
        return Number.isInteger(rowIndex) && rowIndex >= 0 ? rowIndex : 0;
    }

    private getMagramaRowState(rowIndex: number): TratamientosControlMagramaRowState {
        const normalizedRowIndex = this.normalizeRepeatableRowIndex(rowIndex);
        const existing = this.magramaRows.get(normalizedRowIndex);

        if (existing) {
            return existing;
        }

        const state: TratamientosControlMagramaRowState = {
            usos: createDropdownData<string>(),
            plagas: createDropdownData<string>(),
            allPlagas: [],
            allUds: [],
            allDosis: [],
            uds: [],
            dosis: [],
            plagasByUso: {},
            udsByUso: {},
            dosisByUso: {},
            productId: '',
            referencia: '',
            lastSelectedUsoKey: '',
        };

        this.magramaRows.set(normalizedRowIndex, state);

        return state;
    }

    private normalizeLookupText(value: unknown): string {
        return String(value ?? '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim()
            .replace(/\s+/g, ' ')
            .toLowerCase();
    }
}
